import React, { useEffect, useContext } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Table,
  TableContainer,
  Paper,
} from "@mui/material";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { Add, Delete } from "@mui/icons-material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import { useSnackbar } from "notistack";

export const EditCitationDialog = ({
  open,
  onClose,
  onSave,
  citationName,
  citationData,
}) => {
  const { darkMode } = useContext(ThemeContext);

  const methods = useForm({
    defaultValues: {
      treatmentName: citationName || "",
      citations: citationData || [],
    },
  });

  const { register, handleSubmit, control, watch, setValue } = methods;
  const { enqueueSnackbar } = useSnackbar();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "citations",
    keyName: "keyId",
  });

  // Sync treatmentName when citationData changes
  useEffect(() => {
    setValue("treatmentName", citationName);
    setValue("citations", citationData);
  }, [citationName, citationData, setValue]);

  const onSubmit = (data) => {
    if (data.citations.length < 3) {
      enqueueSnackbar(`You should upload at least 3 citations`, {
        variant: "error",
      });
    } else {
      onSave(data);
      onClose();
    }
  };

  const handleAddCitationRow = () => {
    const currentCitations = watch("citations");

    // Check if any citation is incomplete
    const hasIncomplete = currentCitations.some(
      (c) =>
        !c.title?.trim() ||
        !c.url?.trim() ||
        !c.authorString?.trim() ||
        !c.pubYear?.toString().trim()
    );

    if (hasIncomplete) {
      enqueueSnackbar(`Please fill out all fields before adding a new row.`, {
        variant: "error",
      });
      return;
    }

    append({
      title: "",
      url: "",
      authorString: "",
      pubYear: "",
    });
  };

  const handleRemoveCitationRow = (index) => {
    remove(index);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ fontSize: "30px", pb: 0 }}>Edit Citation</DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ pt: 0 }}>
            <TextField
              label="Treatment Name"
              disabled
              margin="normal"
              fullWidth
              {...register("treatmentName", {
                required: "Treatment Name is required",
              })}
            />

            <TableContainer component={Paper} sx={{ mb: 1 }}>
              <Table>
                <TableHead
                  sx={{
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: darkMode
                      ? getColors.appBarBorderDark
                      : getColors.thumbnailBorderLight,
                  }}
                >
                  <TableRow sx={{ height: "60px" }}>
                    {/* Title */}
                    <TableCell
                      sx={{
                        width: "37%",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        px: 0,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          width: "100%",
                          height: "100%",
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: darkMode
                            ? getColors.listItemTextDark
                            : getColors.thumbnailTextLight,
                          px: "20px",
                        }}
                      >
                        Title
                      </Box>
                    </TableCell>

                    {/* URL */}
                    <TableCell
                      sx={{
                        width: "27%",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        px: 0,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          width: "100%",
                          height: "100%",
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: darkMode
                            ? getColors.listItemTextDark
                            : getColors.thumbnailTextLight,
                          px: "20px",
                        }}
                      >
                        URL
                      </Box>
                    </TableCell>

                    {/* Author */}
                    <TableCell
                      sx={{
                        width: "20%",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        px: 0,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          width: "100%",
                          height: "100%",
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: darkMode
                            ? getColors.listItemTextDark
                            : getColors.thumbnailTextLight,
                          px: "20px",
                        }}
                      >
                        Author
                      </Box>
                    </TableCell>

                    {/* Year */}
                    <TableCell
                      sx={{
                        width: "12%",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                        px: 0,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                          width: "100%",
                          height: "100%",
                          fontFamily: "Poppins",
                          fontWeight: 500,
                          fontSize: "20px",
                          color: darkMode
                            ? getColors.listItemTextDark
                            : getColors.thumbnailTextLight,
                          px: "20px",
                        }}
                      >
                        Year
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell
                      sx={{
                        width: "60px",
                        textAlign: "center",
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {/* ACTION */}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => (
                    <TableRow
                      key={field.keyId}
                      sx={{
                        borderColor: darkMode
                          ? getColors.appBarBorderDark
                          : getColors.thumbnailBorderLight,
                      }}
                    >
                      {["title", "url", "authorString", "pubYear"].map(
                        (key) => (
                          <TableCell
                            sx={{
                              borderWidth: 1,
                              borderStyle: "solid",
                              borderColor: darkMode
                                ? getColors.appBarBorderDark
                                : getColors.thumbnailBorderLight,
                            }}
                            key={key}
                          >
                            <TextField
                              fullWidth
                              margin="dense"
                              {...register(`citations.${index}.${key}`, {
                                required: `${key} is required`,
                              })}
                            />
                          </TableCell>
                        )
                      )}
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleRemoveCitationRow(index)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box mt={2} textAlign="center">
              <Button
                startIcon={<Add />}
                variant="outlined"
                onClick={handleAddCitationRow}
                sx={{
                  borderRadius: "5px",
                  fontFamily: "Poppins",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                Add Citation
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
            <Button
              onClick={onClose}
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                color: darkMode
                  ? getColors.appBarBgLight
                  : getColors.appBarBgDark,
                px: 6,
                py: "11px",
                "&:hover": {
                  background: "none",
                  color: getColors.primaryMain,
                },
                mr: 2,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                px: 6,
                py: "11px",
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
